import {
  Timestamp,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { WhatWork } from '@domain/models/pd-hacks'

function toOptionalDate(value: unknown): Date | undefined {
  if (value instanceof Timestamp) {
    return value.toDate()
  }
  if (value instanceof Date) {
    return value
  }
  return undefined
}

function toOptionalNumber(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return value
  }
  return undefined
}

function isReferenceLike(
  value: unknown,
): value is { id: string; path: string } {
  if (!value || typeof value !== 'object') {
    return false
  }
  if (!('id' in value) || !('path' in value)) {
    return false
  }

  const maybeRef = value as { id?: unknown; path?: unknown }
  return typeof maybeRef.id === 'string' && typeof maybeRef.path === 'string'
}

function refToId(value: unknown): string | undefined {
  if (!isReferenceLike(value)) {
    return undefined
  }
  return value.id
}

function refsToIds(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }

  return value
    .map((item) => refToId(item))
    .filter((item): item is string => Boolean(item))
}

export function fromFirestoreWhatWork(
  docSnap: DocumentSnapshot<DocumentData> | QueryDocumentSnapshot<DocumentData>,
): WhatWork {
  const data = (docSnap.data() ?? {}) as Record<string, unknown>

  return {
    uid: docSnap.id,
    solution: typeof data['solution'] === 'string' ? data['solution'] : undefined,
    description:
      typeof data['description'] === 'string' ? data['description'] : undefined,
    addedById: refToId(data['added_by']),
    createdTime: toOptionalDate(data['created_time']),
    totalVotes: toOptionalNumber(data['total_votes']),
    votedByIds: refsToIds(data['voted_by']),
    positiveRatingsIds: refsToIds(data['positive_ratings']),
    negativeRatingsIds: refsToIds(data['negative_ratings']),
    image: typeof data['image'] === 'string' ? data['image'] : undefined,
    symptomId: typeof data['symptom_id'] === 'string' ? data['symptom_id'] : undefined,
  }
}
